import { Check, ChevronDown, Search } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { cn } from "../lib/cn";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

export interface PickerItem {
  id: string;
  label: string;
  meta?: string;
  group?: string;
  /** Short glyph (e.g. "SF") rendered in a colored square. */
  icon?: string;
  iconBg?: string;
  iconFg?: string;
  disabled?: boolean;
}

interface CommonProps {
  items: PickerItem[];
  placeholder?: string;
  searchPlaceholder?: string;
  /** Rows shown before "keep typing" (default 8). */
  limit?: number;
  width?: number;
  mono?: boolean;
  disabled?: boolean;
  footer?: { label: string; onSelect: () => void };
  /** Extra content under the selected label in the trigger. */
  triggerMeta?: boolean;
  className?: string;
  "aria-label"?: string;
}

export interface SinglePickerProps extends CommonProps {
  multiple?: false;
  value: string | null;
  onValueChange: (id: string, item: PickerItem) => void;
}

export interface MultiPickerProps extends CommonProps {
  multiple: true;
  value: string[];
  onValueChange: (ids: string[], item: PickerItem) => void;
}

export type PickerProps = SinglePickerProps | MultiPickerProps;

function Glyph({ item, size = 20 }: { item: PickerItem; size?: number }) {
  if (!item.icon) return null;
  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        background: item.iconBg ?? "var(--surface-inset)",
        color: item.iconFg ?? "var(--fg)",
      }}
      className="flex shrink-0 items-center justify-center rounded-[5px] text-[9px] font-bold"
    >
      {item.icon}
    </span>
  );
}

/**
 * Searchable select for large lists (connections, teams, channels, members).
 * Type-ahead filters label, meta and group; shows `limit` rows with a "keep typing" hint;
 * Enter picks the first row; groups appear when the query is empty. Multi mode keeps the
 * popover open and toggles membership.
 */
export function Picker(props: PickerProps) {
  const {
    items,
    placeholder = "Choose…",
    searchPlaceholder = `Search ${items.length}…`,
    limit = 8,
    width = 360,
    mono = true,
    disabled,
    footer,
    triggerMeta = true,
    className,
  } = props;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selectedIds = props.multiple ? props.value : props.value ? [props.value] : [];
  const selected =
    !props.multiple && props.value ? items.find((i) => i.id === props.value) : undefined;

  const q = query.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      q
        ? items.filter((i) =>
            `${i.label} ${i.meta ?? ""} ${i.group ?? ""}`.toLowerCase().includes(q),
          )
        : items,
    [items, q],
  );
  const shown = filtered.slice(0, limit);

  const pick = (item: PickerItem) => {
    if (item.disabled) return;
    if (props.multiple) {
      const next = props.value.includes(item.id)
        ? props.value.filter((x) => x !== item.id)
        : [...props.value, item.id];
      props.onValueChange(next, item);
    } else {
      props.onValueChange(item.id, item);
      setOpen(false);
      setQuery("");
    }
  };

  // When the query is empty, items are grouped so that each distinct group renders
  // once (in the order the group first appears) with all of its items underneath —
  // this keeps every group's header key unique by construction, rather than
  // re-emitting a header each time the group value changes while iterating.
  const orderedItems: PickerItem[] = [];
  if (q) {
    orderedItems.push(...shown);
  } else {
    const buckets = new Map<string | undefined, PickerItem[]>();
    for (const item of shown) {
      const bucket = buckets.get(item.group);
      if (bucket) {
        bucket.push(item);
      } else {
        buckets.set(item.group, [item]);
      }
    }
    for (const bucket of buckets.values()) orderedItems.push(...bucket);
  }

  const rows: ReactNode[] = [];
  let lastGroup: string | undefined;
  for (const item of orderedItems) {
    if (!q && item.group && item.group !== lastGroup) {
      lastGroup = item.group;
      rows.push(
        <div
          key={`g-${item.group}`}
          role="presentation"
          className="px-2 pt-1.5 pb-0.5 text-2xs font-semibold tracking-wider text-fg-subtle uppercase"
        >
          {item.group}
        </div>,
      );
    }
    const isSel = selectedIds.includes(item.id);
    rows.push(
      <button
        key={item.id}
        type="button"
        role="option"
        aria-selected={isSel}
        disabled={item.disabled}
        onClick={() => pick(item)}
        className={cn(
          "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left hover:bg-surface-inset disabled:opacity-50",
          isSel && "bg-selected",
        )}
      >
        <Glyph item={item} />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className={cn("truncate text-sm font-medium", mono && "font-mono")}>
            {item.label}
          </span>
          {item.meta ? <span className="truncate text-xs text-fg-subtle">{item.meta}</span> : null}
        </span>
        {isSel ? <Check className="size-3.5 text-accent" aria-hidden /> : null}
      </button>,
    );
  }

  const triggerLabel = props.multiple
    ? props.value.length
      ? `${props.value.length} selected`
      : placeholder
    : (selected?.label ?? placeholder);
  const hasValue = props.multiple ? props.value.length > 0 : Boolean(selected);

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={props["aria-label"]}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={cn(
            "flex h-[32px] w-full items-center gap-2 rounded-sm border bg-surface px-2.5 text-left disabled:opacity-50",
            open ? "border-accent" : "border-border hover:border-border-strong",
            className,
          )}
        >
          {selected ? <Glyph item={selected} /> : null}
          <span className="flex min-w-0 flex-1 flex-col leading-tight">
            <span
              className={cn(
                "truncate text-sm font-medium",
                mono && hasValue && "font-mono",
                !hasValue && "text-fg-subtle",
              )}
            >
              {triggerLabel}
            </span>
            {triggerMeta && selected?.meta ? (
              <span className="truncate text-xs text-fg-subtle">{selected.meta}</span>
            ) : null}
          </span>
          <ChevronDown className="size-3.5 shrink-0 text-fg-subtle" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent style={{ width }} className="p-0">
        <div className="flex items-center gap-2 border-b border-border-subtle px-2.5 py-2 text-fg-subtle">
          <Search className="size-3.5 shrink-0" aria-hidden />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && shown[0]) {
                e.preventDefault();
                pick(shown[0]);
              }
            }}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            className="min-w-0 flex-1 bg-transparent text-sm text-fg outline-none placeholder:text-fg-subtle"
          />
          <span className="font-mono text-xs whitespace-nowrap">{items.length} total</span>
        </div>
        <div
          role="listbox"
          aria-multiselectable={props.multiple || undefined}
          className="flex max-h-[300px] flex-col overflow-auto p-1"
        >
          {rows}
          {shown.length === 0 ? (
            <div className="px-2 py-4 text-center text-sm text-fg-subtle">No matches.</div>
          ) : null}
          {filtered.length > limit ? (
            <div className="px-2 py-1.5 text-xs text-fg-subtle">
              Showing {limit} of {filtered.length} · keep typing to narrow
            </div>
          ) : null}
        </div>
        {footer ? (
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              footer.onSelect();
            }}
            className="w-full border-t border-border-subtle px-2.5 py-2 text-left text-sm font-medium text-accent hover:bg-surface-inset"
          >
            {footer.label}
          </button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
